import { FlowState, IState, newApplicationState } from '@modules/state/state';
import { signal } from '@preact/signals-react';
import { Singleton } from '@visiba/instantiation';
import { v4 as uuid } from 'uuid';

export enum DialogHandlerType {
	Dialog,
}

export interface DialogHandlerSpec<T extends DialogHandlerType> {
	jsx: JSX.Element;
	type: T;
	id: string;
}

export interface DialogHandlerStackStateValue<T extends DialogHandlerType> {
	[key: string]: DialogHandlerSpec<T>;
}

interface Stack<T extends DialogHandlerType> {
	readonly state: IState<DialogHandlerStackStateValue<T>>;
	createSpecification(jsx: JSX.Element): DialogHandlerSpec<T>;
	injectSpecification(spec: DialogHandlerSpec<DialogHandlerType.Dialog>): void;
	remove(): void;
}

/** Stack implementation for modal */
class DialogStack implements Stack<DialogHandlerType.Dialog> {
	public readonly state = newApplicationState<DialogHandlerStackStateValue<DialogHandlerType.Dialog>>({}, FlowState.ACCESSIBLE);
	public currentActiveModal: keyof DialogHandlerStackStateValue<DialogHandlerType.Dialog> | null = null;

	/** Creates a modal specification */
	public createSpecification(jsx: JSX.Element): DialogHandlerSpec<DialogHandlerType.Dialog> {
		const dialog: DialogHandlerSpec<DialogHandlerType.Dialog> = {
			jsx,
			type: DialogHandlerType.Dialog,
			id: uuid(),
		};

		return dialog;
	}

	/** Updates the application state with the new spec, replacing current active modal */
	public injectSpecification(spec: DialogHandlerSpec<DialogHandlerType.Dialog>): void {
		this.currentActiveModal = spec.id;
		this.state.mutate(() => {
			return {
				[spec.id]: spec,
			};
		});
	}

	/** Removes current active modal and updates the state */
	public remove(): void {
		this.state.mutate((prev) => {
			if (this.currentActiveModal !== null) {
				delete prev[this.currentActiveModal];

				this.currentActiveModal = null;
			}

			return prev;
		});
	}
}

@Singleton()
export class DialogHandlerController {
	public readonly renderGlobal = signal(true);
	public readonly dialogStack = new DialogStack();

	// May only be one modal present at once
	public get currentActiveModal(): keyof DialogHandlerStackStateValue<DialogHandlerType.Dialog> | null {
		return this.dialogStack.currentActiveModal;
	}

	constructor() {
		// Empty
	}

	public addDialog(jsx: JSX.Element): void {
		this.shift();
		const dialog = this.dialogStack.createSpecification(jsx);
		this.dialogStack.injectSpecification(dialog);
	}

	public shift(): void {
		this.dialogStack.remove();
	}

	public setGlobalRender(): void {
		this.renderGlobal.value = true;
	}

	public setScopedRender(): void {
		this.renderGlobal.value = false;
	}
}
